/* eslint-disable @typescript-eslint/consistent-type-assertions */

import * as React from "react";
import * as H from "history";
import ActionList from "components/ActionList/ActionList";
import AdvancedFilterLayout from "components/AdvancedFilterLayout/AdvancedFilterLayout";
import InfrastructureLayout from "../InfrastructureLayout";
import InternalRedirect from "../../../../components/Navigation/InternalRedirect/InternalRedirect";
import { BaseInfrastructureFilter } from "./MachineFilter";
import MachineHealthStatusHelper from "utils/MachineHealthStatusHelper";
import PaperLayout from "components/PaperLayout/PaperLayout";
import RequestRaceConditioner from "utils/RequestRaceConditioner";
import routeLinks from "../../../../routeLinks";
import { AdvancedFilterCheckbox, FilterSection } from "components/AdvancedFilterLayout";
import { DataBaseComponent, DataBaseComponentState } from "components/DataBaseComponent/DataBaseComponent";
import { IQuery, convertQueryToQueryString } from "components/QueryStringFilters/QueryStringFilters";
import { RouteComponentProps } from "react-router";
import { SummaryResource } from "client/resources";
import { EndpointCommunicationStyleMultiSelect, MachineModelHealthStatusMultiSelect } from "components/MultiSelect";
import EndpointsHelper from "utils/EndpointsHelper/EndpointsHelper";
import ConfirmTentacleUpgradePanel from "./ConfirmTentacleUpgradePanel";

export interface MachinesLayoutProps<Filter, Query> extends RouteComponentProps {
    title: string;
    itemDescriptions: string;
    hasMachines: boolean;
    defaultFilter: Filter;
    initialFilter: Filter;
    filterToQuery: (filter: Filter) => Query;
}

export interface MachinesLayoutState<Filter> extends DataBaseComponentState {
    currentPageIndex: number; // This has a custom endpoint, so we manage our own paging implementation in List/onLoadMore.
    expanded: boolean; // Need to know if we're currently expanded so we can choose to reload when the filter changes or not.
    healthStatusFilters: string[];
    isDisabledFilter: boolean;
    filter: Filter;
    queryFilter: Filter;
    isSearching: boolean;
    redirectToTaskId: string | null;
}

export abstract class BaseMachinesLayout<Props extends MachinesLayoutProps<Filter, Query>, State extends MachinesLayoutState<Filter>, Filter extends BaseInfrastructureFilter, Query extends IQuery> extends DataBaseComponent<Props, State> {
    protected machineHealthStatuses = MachineHealthStatusHelper.getMachineModelHealthStatusResources();
    protected communicationStyles = EndpointsHelper.getCommunicationStyleResources();
    protected requestRaceConditioner = new RequestRaceConditioner();
    protected commonInitialState = {
        redirectToTaskId: null,
        currentPageIndex: 0,
        expanded: true,
        healthStatusFilters: [],
        isDisabledFilter: false,
        isSearching: false,
    };

    constructor(props: Props) {
        super(props);
    }

    render() {
        const redirect = this.state.redirectToTaskId;
        if (typeof redirect === "string") {
            return <InternalRedirect to={routeLinks.task(redirect).root} push={true} />;
        }

        const actionSection = <ActionList actions={this.getActions()} />;
        const searchHintText = "Search " + this.props.itemDescriptions + "...";
        const summaries = this.getSummaries();

        const tentacleUpgradesRequiredWarning = summaries && summaries.TentacleUpgradesRequired === true && (
            <ConfirmTentacleUpgradePanel
                doBusyTask={this.doBusyTask}
                calloutDescriptionElement={<p>One or more {this.props.itemDescriptions} are running old versions of the Tentacle agent and can be upgraded.</p>}
                onTentacleUpgradeComplete={(taskId) => {
                    this.setState({ redirectToTaskId: taskId });
                }}
            />
        );
        return (
            <InfrastructureLayout {...this.props}>
                <PaperLayout title={this.props.title} busy={this.state.busy} errors={this.errors} sectionControl={actionSection}>
                    {summaries && this.props.hasMachines && this.renderPageSummary()}
                    {summaries && !this.props.hasMachines && this.renderOnboarding()}
                    {tentacleUpgradesRequiredWarning}
                    {this.props.hasMachines && (
                        <AdvancedFilterLayout<Filter>
                            filterSections={this.getFilterSections()}
                            filter={this.state.filter}
                            queryFilter={this.state.queryFilter}
                            defaultFilter={this.props.defaultFilter} // Base doesn't decide which kind of filter is passed down
                            initiallyShowFilter={this.isFiltering()}
                            additionalHeaderFilters={this.getNameFilter(searchHintText)}
                            onFilterReset={(filter: Filter) => {
                                this.setState({ filter }, () => {
                                    this.onFilterChange();
                                    const location = { ...(this.props.history as H.History), search: "" };
                                    this.props.history.replace(location);
                                });
                            }}
                            renderContent={() => this.renderMachinesExpander()}
                        />
                    )}
                </PaperLayout>
            </InfrastructureLayout>
        );
    }

    protected abstract loadSummaries(): Promise<void>;
    protected abstract getSummaries(): SummaryResource;
    protected abstract isFiltering(): boolean;

    protected abstract getActions(): JSX.Element[];
    protected abstract renderOnboarding(): JSX.Element;
    protected abstract renderMachinesExpander(): React.ReactNode;
    protected abstract getNameFilter(searchHintText: string): JSX.Element[];

    protected renderPageSummary(): JSX.Element {
        return <React.Fragment />;
    }

    protected getFilterSections() {
        const filters: React.ReactNode[] = [
            <AdvancedFilterCheckbox
                key="filterDisabled"
                label="Disabled only"
                value={this.state.filter.isDisabled}
                onChange={(x) => {
                    this.setFilterState({ isDisabled: x }, () => {
                        this.onFilterChange();
                    });
                }}
            />,
        ];

        filters.push(this.extraFilters());

        filters.push(
            <MachineModelHealthStatusMultiSelect
                key="filterStatus"
                items={this.machineHealthStatuses}
                value={this.state.filter.healthStatuses}
                onChange={(x) => {
                    this.setFilterState({ healthStatuses: x }, () => {
                        this.onFilterChange();
                    });
                }}
            />
        );

        filters.push(
            <EndpointCommunicationStyleMultiSelect
                key="filterCommunication"
                items={this.communicationStyles}
                value={this.state.filter.commStyles}
                onChange={(x) => {
                    this.setFilterState({ commStyles: x }, () => {
                        this.onFilterChange();
                    });
                }}
            />
        );

        const filterSections: FilterSection[] = [
            {
                render: <div>{filters}</div>,
            },
        ];

        return filterSections;
    }

    protected extraFilters(): React.ReactNode[] {
        return [];
    }

    protected setFilterState<K extends keyof Filter>(state: Pick<Filter, K>, callback?: () => void) {
        this.setState(
            (prev) => ({
                filter: { ...(prev.filter as object), ...(state as object) },
            }),
            callback
        );
    }

    protected onFilterChange() {
        const queryString = convertQueryToQueryString(this.props.filterToQuery(this.state.filter));

        if (this.props.location.search !== queryString) {
            const location = { ...this.props.history, search: queryString };
            this.props.history.replace(location);
        }

        this.setState({ isSearching: true }, async () => {
            await this.doBusyTask(() => this.loadSummaries());
            this.setState({ isSearching: false });
        });
    }
}
